import { Component, OnInit, OnChanges, AfterViewChecked, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { QueryList, ViewChildren } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { imagePickerModal } from '@app/modules/common/image-picker';

import { AppConstants } from '@app/app.constants';
import { GenericUtils } from '@app/app.commonutils';

import { Place } from '@app/modules/model/place';
import { Product } from '@app/modules/model/product';
import { User } from '@app/modules/model/user';
import { ProductOption } from '@app/modules/model/productoption';
import { ProductCategory } from '@app/modules/model/productcategory';
import { ProductSelect } from '@app/modules/model/productselect';
import { Family } from '@app/modules/model/family';
import { FamilyProduct } from '@app/modules/model/familyproduct';
import { FamilyPeriod } from '@app/modules/model/period';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';
import { httpService } from '@app/modules/common/http';

/************************************/
/* PRODUCT COMPONENT                */
/************************************/

@Component({
    selector: 'ui-product-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiProductEdit extends languageSupport implements OnInit, OnChanges, AfterViewChecked, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('product') _item: Product = null; 
    @Input('parent') _parent: Product = null; 
    @Input('user') _user: User = null;

    normalize(str){
        return GenericUtils.Normalize(str);
    }

    get TaxRate1(){     // IVA superreducido
        return AppConstants.TaxRate1;
    }

    get TaxRate2(){     // IVA reducido
        return AppConstants.TaxRate2;
    }

    get TaxRate3(){     // IVA general
        return AppConstants.TaxRate3;
    }

    @ViewChild('scrollable', {static: false}) _scrollable: ElementRef;
    private _view = {
        scroll: 0,
        target: 'PRODUCT'   // 'PRODUCT' / 'CATEGORY' / 'ONECLICK';
    };

    get View(){
        return this._view.target;
    }

    set View(value){
        if (value != this._view.target){
            if (this._scrollable){
                if (value == 'PRODUCT') {
                    this._scrollable.nativeElement['scrollTop'] = this._view.scroll;  
                    setTimeout(() => {  // in case height is not enough, wait for refresh
                        this._scrollable.nativeElement['scrollTop'] = this._view.scroll;                
                    }, 0);
                }
                else {
                    this._view.scroll = this._scrollable.nativeElement['scrollTop'];
                }      
            }
            this._view.target = value;
        }

        this.change.markForCheck();
    }

    get CanAvailable(){
        return this._place.AllowAvailability(this._user);
    }

    get CanModify(){
        return this._place.AllowProductManage(this._user);
    }

    private _editable: Product = null;
    get editable() {
        return this._editable;
    }

    private _category: ProductCategory = null;
    get category() {
        return this._category;
    }

    private _oneclick: ProductSelect = null;
    get oneclick(){
        return this._oneclick;
    }

    // Creación rápida desde el editor de atajo: valor centinela del desplegable de
    // un grupo SINGLE para "crear una opción nueva" (en vez de elegir una existente),
    // y familia opcional a la que añadir el atajo al aceptar.
    readonly QUICK_NEW = '__NEW__';
    _quickFamily: Family = null;                  // familia elegida en el desplegable (por atajo)
    // Asociaciones atajo→familia PENDIENTES (alta rápida): no se tocan las familias
    // reales hasta el commit del producto (_CommitProduct), para no dejar un item
    // colgando si se cancela el editor exterior.
    private _quickFamilyLinks: Array<{ family: Family, select: ProductSelect }> = [];
    private _lastAutoName = '';                   // último nombre autollenado en el atajo (para
                                                  // copiar el nombre COMPLETO de la opción nueva
                                                  // mientras el usuario no lo edite a mano)

    // ion-select compara por identidad de objeto (el centinela QUICK_NEW es un string).
    compareOption = (a, b) => (a === b);
    compareFamily = (a, b) => (a && b ? (a.objid == b.objid) || (a === b) : a === b);

    // Form del holder "opción nueva" (nombre + precio) de un grupo SINGLE. Se teclea
    // con kb-input (numpad) igual que el editor de opciones. Solo precio positivo.
    private _newOptionForm(){
        return new FormGroup({
            name: new FormControl('', [ Validators.required ]),
            price: new FormControl(null, [])
        });
    }

    // Familias del local para el desplegable "Añadir a familia".
    get QuickFamilies(): Array<Family> {
        return (this._place ? this._place.families : []).filter(f => f && f.IsValid);
    }

    CanAddFamily(){
        return (this._parent == null);  // top level group
    }

    get IsGroup(){
        return this._item && this._editable && (this._editable.isgroup == true) && (this._editable.isfamily == false);
    }

    get IsFamily(){
        return this._item && this._editable && (this._editable.isgroup == true) && (this._editable.isfamily == true);
    }

    get IsGeneric(){
        return this._item && this._editable && (this._editable.generic == true);
    }

    get IsProduct(){
        return this._item && this._editable && (!this.IsGroup && !this.IsFamily && !this.IsGeneric);
    }

    get TargetForm(){
        if (this.IsGroup){
            return this.GroupForm;
        }

        if (this.IsFamily){
            return this.GroupForm;            
        }

        if (this.IsGeneric){
            return this.GenericForm;            
        }

        if (this.IsProduct){
            let _form = this.ProductForm;

            switch(this.View){
                case 'CATEGORY':
                    _form = this.CategoryForm;
                    break;
                case 'ONECLICK':
                    _form = this.OneClickForm;
                    break;
            }

            return _form;
        }
    
        return null;
    }

    private _editmode = false;
    get EditMode(){
        // check if we are in the item selection
        if (this._item == null){
            return false;
        }

        // check if it is a new product / group
        if (this._item.ToInsert || this._item.IsCatalog) {
            return true;        
        }

        // check if the product has been updated
        if (!this._editable.IsPurchased){
            return false;
        }

        // check ownership for groups
        if (this._editable.isgroup && this.CanModify){
            return true;        
        }

        // explicit edit mode requested
        return this._editmode;      
    }

    set EditMode(value){
        this._editmode = value;

        if (this.IsProduct){
            this.EditMode ? this.ProductForm.get('market').enable() : this.ProductForm.get('market').disable();
            this.EditMode ? this.ProductForm.get('weight').enable() : this.ProductForm.get('weight').disable();    
        }

        this.change.markForCheck();
    }

    get HeaderColor(){
        if (this._item == null){
            return 'dark'
        }

        if (this._item.ToInsert){
            return 'primary'
        }

        switch(this._item.status){
            case 'AC': return 'success';
            case 'UN': return 'warning';
            case 'DE': return 'danger'
        } 

       return 'primary';
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    private OnSelect(_item){
        this._onPanel.emit({
            scnd: 'product',
            args: {
                product: _item
            }
        });
    }

    private _nophoto = '/assets/image/product.png';
    public cardTextColor = '#000000';
    public cardBackgroundColor = '#ffcc00';
    public creatingNameCard = false;
    public searchingSquareImage = false;
    public showCardColors = true;

    // Tamaño de letra de la tarjeta de TEXTO. null = Auto (se ajusta solo para que
    // quepa, como siempre). Un número es el tamaño de PARTIDA (en px sobre el lienzo
    // 1500x1500); si el texto no cabe, se reduce igualmente para no desbordar.
    public cardFontSize: number = null;
    public readonly cardFontSizes = [ 120, 160, 200, 260, 320, 400 ];

    /**********************************/
    /* GROUP FORM                     */
    /**********************************/

    OnCreateGroup(){
        let _group = new Product(null, this.data);
        if (_group){
            _group.place = this._place;
            _group.parent = this._parent;
            _group.generic = false;
            _group.isgroup = true;
            _group.family = null;
            _group.status = 'AC';
        }

        this.OnSelect(_group);
    }

    OnCreateFamily(){
        let _group = new Product(null, this.data);
        if (_group){    
            _group.place = this._place;
            _group.generic = false;
            _group.isgroup = true;
            _group.family = null;   // will be set below
            _group.status = 'AC';
        }

        let _family = new Family(null, this.data);
        if (_family){
            _family.product = _group;
            _family.allday = false;
            _family.status = 'AC'

            _group.family = _family;
        }

        this.OnSelect(_group);
    }

    private _groupForm = null;
    get GroupForm(){
        if (this._groupForm == null){
            this.LoadGroupForm();
        }
        return this._groupForm;
    }

    private ValidatePeriods(control: FormControl) {
        let _isvalid = control.value.every(period => {
            return period.IsValid;
        })

        return _isvalid ? null : {'period': {valid: false}};
    }

    private FamilyPeriods(){
        let _familyperiods = [];

        for (let _familyperiod of this._editable.family.periods) {
            if (_familyperiod.IsValid){
                _familyperiods.push(_familyperiod);
            }
        }

        return _familyperiods;
    }

    private FamilyItems() {
        let _familyitems = [];

        for (let _familyitem of this._editable.family.items) {
            if (_familyitem.IsValid){
                _familyitems.push(_familyitem);
            }
        }

        _familyitems.sort((a, b) => {
            return Number(a.sort) - Number(b.sort);
        });

        return _familyitems;
    }

    private LoadGroupForm(){
        let _photo = this._editable.avatar || this._nophoto;
        let _name = this._editable.name;
        let _description = this._editable.description;
        let _isfamily = this._editable.isfamily;

        // group family items
        let _allday = false;
        let _periods = []; 
        let _fmitems = [];

        if (_isfamily){
            _allday = this._editable.family.allday;
            _periods = this.FamilyPeriods();
            _fmitems = this.FamilyItems();
        }

        if (this._groupForm == null){
            this._groupForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators required
                ]),
                isfamily: new FormControl(_isfamily, [
                    // no validators required
                ]),
                allday: new FormControl(_allday, [
                    // no validators required
                ]),
                periods: new FormControl(_periods, [
                    // no validators required
                ]),
                fmitems: new FormControl(_fmitems, [
                    // no validators required
                ])
            });
        }
        else {
            this._groupForm.patchValue({
                photo: _photo,
                name: _name,
                description: _description,
                isfamily: _isfamily,
                allday: _allday,
                periods: _periods,
                fmitems: _fmitems
            });
        }

        this.OnFamilyCheck();

        this._groupForm.markAsPristine();
        setTimeout(() => {
            this._groupForm.markAsPristine();
        }, 0);
    }

    OnFamilyCheck(){
        let _periods = this.GroupForm.get('periods');
        let _fmitems = this.GroupForm.get('fmitems');

        let _checked = this.GroupForm.get('isfamily').value;
        if (_checked){
            if (_periods){
                if (this.GroupForm.get('allday').value){
                    _periods.clearValidators();
                    _periods.setErrors(null);
                    _periods.updateValueAndValidity();
                }
                else {
                    _periods.setValidators([ Validators.required, this.ValidatePeriods.bind(this) ]);
                    _periods.setErrors(null);
                    _periods.updateValueAndValidity();
                }            
            }
            
            if (_fmitems){
                _fmitems.setValidators([ Validators.required ]);
                _fmitems.setErrors(null);
                _fmitems.updateValueAndValidity();
            }
        }
        else {
            if (_periods){
                _periods.clearValidators();
                _periods.setErrors(null);
                _periods.updateValueAndValidity();
            }

            if (_fmitems){
                _fmitems.clearValidators();
                _fmitems.setErrors(null);
                _fmitems.updateValueAndValidity();
            }
        }
    }

    /**********************************/
    /* GENERIC FORM                   */
    /**********************************/

    OnCreateGeneric(){
        let _generic = new Product(null, this.data);
        if (_generic){
            _generic.place = this._place;
            _generic.parent = this._parent;
            _generic.generic = true; 
            _generic.isgroup = false;
            _generic.family = null;
            _generic.status = 'AC';
        }

        this.OnSelect(_generic);
    }

    private _genericForm = null;
    get GenericForm(){
        if (this._genericForm == null){
            this.LoadGenericForm();
        }
        return this._genericForm;
    }

    private LoadGenericForm(){
        let _photo = this._editable.avatar || this._nophoto;
        let _name = this._editable.name;
        let _description = this._editable.description;
        let _type = this._editable.type;

        if (this._genericForm == null){
            this._genericForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                type: new FormControl(_type, [
                    Validators.required 
                ])
            });
        }
        else {
            this._genericForm.patchValue({
                photo: _photo,
                name: _name,
                description: _description,
                type: _type
            });
        }

        this._genericForm.markAsPristine();
        setTimeout(() => {
            this._genericForm.markAsPristine();
        }, 0);
    }

    /**********************************/
    /* CATEGORY FORM                  */
    /**********************************/

    private ValidateCategoryName(control: FormControl): any {
        let _usedname = control.value;
        if (_usedname){
            for(let _category of this['self']._editable.categories){
                if ((_category.IsValid) && (_usedname == _category.name) && (_usedname != this['category'].name)){
                    return { available: true };    
                }
            }
        }
        
        return null;
    }

    private ValidateCategoryForm(group: FormGroup):any {
        let _type = group.get('type');
        
        let _fxmin = group.get('fxmin');
        let _fxmax = group.get('fxmax');
        
        if (_type && _fxmin){
            if (_type.value == 'FIXED'){
                let _usedmin = _fxmin.value || 0;
                let _usedmax = _fxmax.value || 0;
                
                if (isNaN(parseInt(_usedmin)) || (Number(_usedmin) < 0) || (Number(_usedmin) > this['category'].options.length)){
                    _fxmin.setErrors({ invalid: true });   
                }            
                
                if (isNaN(parseInt(_usedmax)) || (Number(_usedmax) < 1) || (Number(_usedmax) < Number(_usedmin)) || (Number(_usedmax) >  ((this['category'].options.length == 0) ? 1 : this['category'].options.length))){
                    _fxmax.setErrors({ invalid: true });   
                }            
            }
            else {
                _fxmin.setErrors(null);
                _fxmax.setErrors(null);
            }
        }
        
        return null;
    }
   
    private _CategoryForm(category){
        let _form = ('form' in category) ? category['form'] : null;
        if (!_form){
            let _name = category.name;
            let _type = category.type;
            let _fxmin = category.fxmin;
            let _fxmax = category.fxmax;
            let _cond = (category.depends) && (category.depends.length > 0);

            let _bind = {
                self: this,
                category: category
            };

            _form = new FormGroup({
                name: new FormControl(_name, [ 
                    Validators.required,
                    this.ValidateCategoryName.bind(_bind)
                ]),
                type: new FormControl(_type, [ 
                    Validators.required 
                ]),
                fxmin: new FormControl(_fxmin, [ 
                    // no validators
                ]),
                fxmax: new FormControl(_fxmax, [ 
                    // no validators
                ]),
                cond: new FormControl(_cond, [ 
                    // no validators
                ])
            }, this.ValidateCategoryForm.bind(_bind));
        }

        return _form;
    }

    get CategoryForm(){
        if (this._category){
            return this._category['form'];
        }
        return null;
    }

    /**********************************/
    /* OPTION FORM                    */
    /**********************************/

    private ValidateOptionForm(group: FormGroup):any {
        let _form = this['self']._productForm;
        if (_form){
            setTimeout(() => {
                let _control = _form.get('categories');
                if (_control){
                    _control.setErrors(group.invalid ? { options: true } : null)
                }    
            }, 0);

            if (group.dirty){
                _form.markAsDirty();
            }
        }

        return null;
    }

    private OptionForm(option){
        let _form = ('form' in option) ? option['form'] : null;
        if (!_form){
            let _name = option.name;
            let _negate = (option.price < 0);
            let _price = Math.abs(option.price);
            let _check = (option.status != 'CT');
            let _desc =  option.description;
            
            let _bind = {
                self: this,
                option: option
            };

            _form = new FormGroup({
                name: new FormControl(_name, [
                    Validators.required 
                ]),
                negate: new FormControl(_negate, [
                    // no validators
                ]),
                price: new FormControl(_price, [
                    // no validators
                ]),
                check: new FormControl(_check, [
                    // no validators
                ]),
                desc: new FormControl(_desc, [
                    // no validators
                ])
            }, this.ValidateOptionForm.bind(_bind));
        }
        return _form;
    }

    /**********************************/
    /* ONECLICK FORM                  */
    /**********************************/
   
    private _OneClickForm(select){
        let _form = ('form' in select) ? select['form'] : null;
        if (!_form){
            let _photo = select.photo || select.avatar || this._nophoto;
            let _name = select.name;
            let _code = select.code;
            let _description = select.description;
            
            _form = new FormGroup({
                name: new FormControl(_name, [ 
                    Validators.required
                ]),
                code: new FormControl(_code, [
                    Validators.maxLength(50)
                ]), 
                description: new FormControl(_description, [ 
                    // no validators
                ]),
                photo: new FormControl(_photo, [ 
                    // no validators
                ])
            });
        }

        select['form'] = _form;
    }

    get OneClickForm(){
        if (this._oneclick){
            return this._oneclick['form'];
        }
        return null;
    }

    /**********************************/
    /* PRODUCT FORM                   */
    /**********************************/

    OnCreateProduct(){
        let _product = new Product(null, this.data);
        if (_product){
            _product.place = this._place;
            _product.parent = this._parent;
            _product.generic = false; 
            _product.isgroup = false;
            _product.family = null;
            _product.status = 'AC';
        }

        this.OnSelect(_product);
    }

    private _productForm = null;   
    get ProductForm(){
        if (this._productForm == null){
            this.LoadProductForm();
        }
        return this._productForm;
    }

    private ValidateProductForm(group: FormGroup):any {
        let _control = group.get('options');
        if (_control) {
            _control.setErrors(null);
            for(let _category of this._editable.categories){
                for(let _option of _category.options){
                    if ((_option.active) && (_option['form'].invalid)){
                        _control.setErrors({ invalid: true });
                    }
                }
            }
        }

        return null;
    }

    OnMarketPriceChanged(){
        let _pricecontrol = this.ProductForm.get('price');

        if (this.ProductForm.get('market').value == true){
            _pricecontrol.clearValidators();
            _pricecontrol.setErrors(null);
            _pricecontrol.updateValueAndValidity();    
        }
        else {
            _pricecontrol.setValidators([ Validators.required ]);
            _pricecontrol.setErrors({ required: true });
            _pricecontrol.updateValueAndValidity();    
        }        

        if (this.ProductForm.get('market').value == true){
            this.ProductForm.get('weight').setValue(true);
        }

        this.change.markForCheck();
    }

    get taxtext(){
        switch(this._editable.taxrate){
            case this.TaxRate1:
                return this.lang.tr('@place_input_taxrate_1') + " (" + this.TaxRate1 + "%)";
            case this.TaxRate2:
                return this.lang.tr('@place_input_taxrate_2') + " (" + this.TaxRate2 + "%)";
            case this.TaxRate3:
                return this.lang.tr('@place_input_taxrate_3') + " (" + this.TaxRate3 + "%)";
        }

        if (this._editable.taxrate === 0){
            return this.lang.tr('@place_input_taxrate_exempt') + " (0%)";
        }

        return null;
    }

    set taxtext(value){
        // nothing to do
    }

    private LoadProductForm(){
        let _photo = this._editable.avatar || this._nophoto;
        let _name = this._editable.name;
        let _code = this._editable.code;
        let _price = this._editable.price;
        // taxrate=0 (Exento) es valido — chequear null/undefined explicito.
        let _taxcheck = (this._editable.taxrate !== null && this._editable.taxrate !== undefined);
        let _taxrate = this._editable.taxrate;
        let _market = this._editable.MarketPrice;
        let _weight = this._editable.IsWeighted;
        let _description = this._editable.description
        let _type = this._editable.type;

        let _categories = this._editable.categories;
        for(let _category of _categories){
            _category['form'] = this._CategoryForm(_category);
            _category.product = this._editable;

            for(let _option of _category.options){
                _option.category = _category;
                _option['edit'] = false;
                _option['form'] = this.OptionForm(_option);
                _option.product = this._editable;
            }
        }

        let _preselects = this._editable.selects;
        for (let _preselect of _preselects){
            _preselect.product = this._editable;
            this._OneClickForm(_preselect);
        }

        if (!this._productForm){
            this._productForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                code: new FormControl(_code, [ 
                    Validators.maxLength(50)
                ]),
                taxcheck: new FormControl(_taxcheck, [ 
                    // no validators required
                ]),
                taxrate: new FormControl(_taxrate, [ 
                    // no validators required
                ]),
                market: new FormControl(_market, [ 
                    // no validators required
                ]),
                weight: new FormControl(_weight, [ 
                    // no validators required
                ]),
                price: new FormControl(_price, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators required
                ]),
                type: new FormControl(_type, [
                    Validators.required 
                ]),
                options: new FormControl(null, [
                    // no validators required
                ])
            }, this.ValidateProductForm.bind(this));
        }
        else {
            this._productForm.patchValue({
                photo: _photo, 
                name: _name,
                code: _code,
                taxcheck: _taxcheck,
                taxrate: _taxrate,
                market: _market,
                weight: _weight,
                price: _price,
                description: _description,
                type: _type
            });  
        }
    
        this.OnMarketPriceChanged();

        this._productForm.markAsPristine();
        setTimeout(() => {
            this._productForm.markAsPristine();      
        }, 0);

        this.EditMode = false;
    }

    /**********************************/
    /* STARTS HERE                    */
    /**********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService, private http: httpService, private modalCtrl: ModalController) {
        super(lang, change);
    }

    OnViewChanged(){
        this.change.markForCheck();
    }

    private _isonchange = false;    // avoid close confirmation when quick changing products 

    private _CanClose(){
        return new Promise(async (resolve) => {
            let _form = this.TargetForm;
            if (_form && _form.dirty && !this._isonchange){
                console.log("[EDIT PRODUCT] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        _form.markAsPristine();
                    }

                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });
    }

    private _close_subscription = null;
    private _user_subscription = null;
    private _theme_subscription = null;

    ngOnInit(){
        this._user_subscription = this._user.OnRefresh.subscribe(
        data => {
            this.change.markForCheck();
        });

        this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            this.view.OnCloseResponse(await this._CanClose());
        });

        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    private _subscriptions = [];
    ngOnChanges(){
        this._isonchange = true;

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        this._quickFamily = null;
        this._quickFamilyLinks = [];   // nuevo producto en edición: sin familias pendientes

        if (this._item){
            this._editable = this._item.Copy();
            this._subscriptions.push(
                this._editable.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            );
    
            this._editable.IsCatalog = false;
            this._editable.Catalog = this._item.Catalog;
            
            if (this._item.IsCatalog){   // editing a catalog product
                this._editable.Catalog = this._item;
            }        
    
            if (this._editable.isgroup){
                this.LoadGroupForm();
            }
            else {
                if (this._editable.IsGeneric){
                    this.LoadGenericForm();
                }
                else {
                    this.LoadProductForm();
                }
            }
    
            this._subscriptions.push(
                this._item.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            )
    
            this.SortCategories(false);
            this._subscriptions.push(
                this._editable.OnRefresh.subscribe(
                data => {
                    this.SortCategories(false);
                })
            );    
        }
        else {
            this._editable = null;
        }

        this.SelectProducts = null;
        this.EditMode = false;          
        this.View = 'PRODUCT';    

        this.change.markForCheck();
        setTimeout(() => {
            this._isonchange = false;
        }, 0)
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    private _OnClose(){
        this._place.DoRefresh('PRODUCT', true);

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this.view.RightPanel){
            this._onPanel.emit({
                scnd: null,
                args: null
            });    
        }
        else {
            this._onPanel.emit({
                scnd: null
            });

            // wait for animation to finish
            setTimeout(() => {
                this._onPanel.emit({
                    args: null
                });    
            }, 300);
        }    
    }

    private _OnBack(){
        // scenario 1: not viewing the product
        if (this.View != 'PRODUCT'){
            this.View = 'PRODUCT';

            if (this._category){
                this.CategoryForm.markAsPristine();
                this._category = null;    
            }

            if (this._oneclick){
                this.OneClickForm.markAsPristine();
                this._oneclick = null;    
            }
        }
        else {
            // scenario 2: viewing the product on edit mode        
            if ((this.EditMode) && (this._editmode)) {
                this.ngOnChanges();

                let _form = this.TargetForm;
                if (_form){
                    _form.markAsPristine();
                }

                this._scrollable.nativeElement.scrollTop = 0;
                this.EditMode = false;
            }

            // scenario 3: not in edit mode: exit the panel
            else {
                this._OnClose();
            }
        }

        this.change.markForCheck();
    }

    async OnClose(){
        let _form = this.TargetForm;
        if (_form && _form.dirty){
            console.log("[EDIT PRODUCT] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnBack();
                }
            });

            await alert.present();        
        }
        else {
            this._OnBack();
        }
    }

    OnEnableEdition(){
        this.EditMode = true;
    }

    OnNameChange(){
        let _form = this.TargetForm;

        if (_form && (_form.get('photo').value == this._nophoto)){
            let _pname = _form.get('name').value;
            let _photo = this._editable.avatarPhoto(_pname);
            _form.get('photo').setValue(_photo);
        }
    }

    get CanCreateSmartAsset(){
        let _name = this.ProductForm?.get('name')?.value;
        return !!this._safeCardName(_name);
    }

    get CanCreateSmartAssetOneClick(){
        let _name = this.OneClickForm?.get('name')?.value;
        return !!this._safeCardName(_name);
    }

    get CanCreateSmartAssetGroup(){
        let _name = this.GroupForm?.get('name')?.value;
        return !!this._safeCardName(_name);
    }

    get CanCreateSmartAssetGeneric(){
        let _name = this.GenericForm?.get('name')?.value;
        return !!this._safeCardName(_name);
    }

    private _safeCardName(name: string){
        let _normalized = (name || '').trim();
        _normalized = _normalized.replace(/[_]+/g, ' ');
        _normalized = _normalized.replace(/[\/\\-]+/g, ' ');
        _normalized = _normalized.replace(/\s+/g, ' ');
        return _normalized.trim();
    }

    private _colorWithAlpha(color: string, alpha = 1){
        let _hex = (color || '#000000').replace('#', '').trim();
        if (_hex.length === 3){
            _hex = _hex.split('').map(ch => ch + ch).join('');
        }
        let _num = Number.parseInt(_hex, 16);
        if (Number.isNaN(_num)){
            return `rgba(0, 0, 0, ${alpha})`;
        }

        let _r = (_num >> 16) & 255;
        let _g = (_num >> 8) & 255;
        let _b = _num & 255;
        return `rgba(${_r}, ${_g}, ${_b}, ${alpha})`;
    }

    private _buildCardImageDataUrl(text: string, textColor: string, backgroundColor: string, startSize: number = null){
        let _canvas = document.createElement('canvas');
        _canvas.width = 1500;
        _canvas.height = 1500;
        let _ctx = _canvas.getContext('2d');
        if (!_ctx){
            return null;
        }

        _ctx.fillStyle = backgroundColor || '#ffcc00';
        _ctx.fillRect(0, 0, _canvas.width, _canvas.height);

        let _name = this._safeCardName(text);
        if (!_name){
            return null;
        }

        let _words = _name.split(' ').filter(Boolean);
        let _lines = [];
        let _maxLines = 3;
        // Tamaño de partida: el elegido por el usuario (si lo hay) o 200 (Auto). Se
        // acota a >= 72 para que el bucle de ajuste siga funcionando.
        let _fontSize = (startSize && startSize >= 72) ? startSize : 200;
        let _maxWidth = _canvas.width - 80;
        _ctx.textAlign = 'center';
        _ctx.textBaseline = 'top';

        const _tryLines = (size: number) => {
            _ctx.font = `700 ${size}px Arial`;
            let _tmp = [];
            let _current = '';
            for (let _word of _words){
                let _candidate = _current ? `${_current} ${_word}` : _word;
                if (_ctx.measureText(_candidate).width <= _maxWidth){
                    _current = _candidate;
                }
                else {
                    if (_current){
                        _tmp.push(_current);
                    }
                    _current = _word;
                }
            }
            if (_current){
                _tmp.push(_current);
            }
            return _tmp;
        };

        // Cabe si no excede el nº de líneas Y ninguna línea supera el ancho (una
        // palabra larga no se puede partir, así que a tamaños grandes hay que reducir
        // hasta que quepa de ancho; si no, fillText dibujaría fuera del lienzo).
        const _fits = (size: number, lines: string[]) => {
            if (lines.length > _maxLines){
                return false;
            }
            _ctx.font = `700 ${size}px Arial`;
            for (let _line of lines){
                if (_ctx.measureText(_line).width > _maxWidth){
                    return false;
                }
            }
            return true;
        };

        // Floor más bajo (36) para que una palabra muy larga pueda reducirse hasta
        // caber de ancho.
        while (_fontSize >= 36){
            _lines = _tryLines(_fontSize);
            if (_fits(_fontSize, _lines)){
                break;
            }
            _fontSize -= 12;
        }

        if (_lines.length > _maxLines){
            _lines = _lines.slice(0, _maxLines);
        }

        _ctx.font = `700 ${_fontSize}px Arial`;
        let _lineHeight = Math.round(_fontSize * 1.08);
        let _totalHeight = _lineHeight * _lines.length;
        let _y = Math.round((_canvas.height - _totalHeight) / 2);
        let _x = Math.round(_canvas.width / 2);
        let _shadowColor = this._colorWithAlpha('#000000', 0.45);

        for (let _line of _lines){
            _ctx.fillStyle = _shadowColor;
            _ctx.fillText(_line, _x + 6, _y + 6);
            _ctx.fillStyle = textColor || '#000000';
            _ctx.fillText(_line, _x, _y);
            _y += _lineHeight;
        }

        return _canvas.toDataURL('image/jpeg', 0.95);
    }

    /** Lista de candidatos (serper/Bing) sin descargar: { images:[{url,thumb,w,h}] }. */
    private _fetchProductImageList(query: string){
        return new Promise<any>((resolve) => {
            let _target = AppConstants.baseURL + 'common/product_image_search.php?mode=list&q=' + encodeURIComponent(query);
            let _subscription = this.http.get(_target).subscribe(
            data => { _subscription.unsubscribe(); resolve(data); },
            () => { _subscription.unsubscribe(); resolve(null); });
        });
    }

    /** Descarga la imagen elegida (por consulta + indice del listado cacheado). */
    private _fetchProductImageChosen(query: string, index: number){
        return new Promise<any>((resolve) => {
            let _target = AppConstants.baseURL + 'common/product_image_search.php?mode=fetch'
                        + '&q=' + encodeURIComponent(query)
                        + '&i=' + index;
            let _subscription = this.http.get(_target).subscribe(
            data => { _subscription.unsubscribe(); resolve(data); },
            () => { _subscription.unsubscribe(); resolve(null); });
        });
    }

    /** Abre el modal con las miniaturas y devuelve el indice elegido (number) o null. */
    private async _pickProductImage(images: any[]){
        const _modal = await this.modalCtrl.create({
            component: imagePickerModal,
            cssClass: 'modal-alert',
            componentProps: { data: {
                header: this.lang.tr('@place_product_search_square_image'),
                empty: this.lang.tr('@place_product_asset_image_failed'),
                closeLabel: this.lang.tr('@image_picker_close'),
                images: images
            }}
        });
        await _modal.present();
        const _res = await _modal.onDidDismiss();
        return (_res && (typeof _res.data === 'number')) ? _res.data : null;
    }

    private async _showAssetInfo(headerTr: string, messageTr: string){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr(headerTr),
            message: this.lang.tr(messageTr),
            buttons: [{
                text: this.lang.tr('@accept'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }]
        });
        await alert.present();
    }

    async OnCreateNameCard(){
        await this._createNameCard(this.ProductForm);
    }

    async OnCreateNameCardOneClick(){
        await this._createNameCard(this.OneClickForm);
    }

    async OnCreateNameCardGroup(){
        await this._createNameCard(this.GroupForm);
    }

    async OnCreateNameCardGeneric(){
        await this._createNameCard(this.GenericForm);
    }

    private async _createNameCard(form: any){
        if (this.creatingNameCard){
            return;
        }

        let _name = form.get('name').value;
        if (!this._safeCardName(_name)){
            await this._showAssetInfo('@place_product_asset_error_title', '@place_product_asset_name_required');
            return;
        }

        this.creatingNameCard = true;
        this.change.markForCheck();
        try {
            let _dataUrl = this._buildCardImageDataUrl(_name, this.cardTextColor, this.cardBackgroundColor, this.cardFontSize);
            if (_dataUrl){
                form.get('photo').setValue(_dataUrl);
                form.markAsDirty();
            }
            else {
                await this._showAssetInfo('@place_product_asset_error_title', '@place_product_asset_card_failed');
            }
        }
        finally {
            this.creatingNameCard = false;
            this.change.markForCheck();
        }
    }

    async OnSearchSquareImage(){
        await this._searchSquareImage(this.ProductForm);
    }

    async OnSearchSquareImageOneClick(){
        await this._searchSquareImage(this.OneClickForm);
    }

    async OnSearchSquareImageGroup(){
        await this._searchSquareImage(this.GroupForm);
    }

    async OnSearchSquareImageGeneric(){
        await this._searchSquareImage(this.GenericForm);
    }

    private async _searchSquareImage(form: any){
        if (this.searchingSquareImage){
            return;
        }

        let _name = this._safeCardName(form.get('name').value);
        if (!_name){
            await this._showAssetInfo('@place_product_asset_error_title', '@place_product_asset_name_required');
            return;
        }

        this.searchingSquareImage = true;
        this.change.markForCheck();
        try {
            // 1) Traer los candidatos (serper/Bing) sin descargar.
            let _list: any = await this._fetchProductImageList(_name);
            let _images = (_list && (_list.errorcode === 0) && Array.isArray(_list.images)) ? _list.images : [];
            if (!_images.length){
                await this._showAssetInfo('@place_product_asset_error_title', '@place_product_asset_image_failed');
                return;
            }

            // 2) El usuario elige entre las miniaturas.
            let _index: number = await this._pickProductImage(_images);
            if (_index === null || _index < 0){
                return;   // cancelo sin cambiar nada
            }

            // 3) Descargar la elegida (con fallback a miniatura cacheada) a base64.
            let _reply: any = await this._fetchProductImageChosen(_name, _index);
            if (_reply && (_reply.errorcode === 0) && _reply.base64){
                let _mime = _reply.mime || 'image/jpeg';
                form.get('photo').setValue(`data:${_mime};base64,` + _reply.base64);
                form.markAsDirty();
                return;
            }

            await this._showAssetInfo('@place_product_asset_error_title', '@place_product_asset_image_failed');
        }
        finally {
            this.searchingSquareImage = false;
            this.change.markForCheck();
        }
    }

    OnPriceChange(option = null){
        let _control = (option) ? option['form'].get('price') : this.ProductForm.get('price');
        if (_control){
            let _value = _control.value;
            if (_value){
                _control.setValue(parseFloat(_value.toString().replace(",", ".")).toFixed(2));
            }
            else {
                _control.setValue(null);
            }   
            _control.markAsDirty(); 
        }

        // apply the changes in the option (will be applied to directs)
        if (option){
            option.price = Number(_control.value);
            let _negate = option['form'].get('negate').value;
            if (_negate) {
                option.price = 0 - option.price;
            }

            this.change.markForCheck();
        }
    }

    /**********************************/
    /* GROUP EDITION                  */
    /**********************************/    

    async OnGroupAvailable(value){
        if (!this.CanAvailable){
            return;     // the user does not have permissions for this action 
        }
        
        if (value == false){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_product_unavailable_title'),
                message: this.lang.tr('@place_product_unavailable_message', [ this._editable.name ]),
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then(async (detail:any) => {
                if (detail.data == true){
                    this.SetProductAvailable(false);
                }
            });

            await alert.present();
        }
        else {
            this.SetProductAvailable(true);
        }
    }

    /**********************************/
    /* FAMILY SPECIFIC                */
    /**********************************/ 

    OnAlldayChange(){
        let _control = this.GroupForm.get('periods');
        if (_control){
            if (this.GroupForm.get('allday').value){
                _control.clearValidators();
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }
            else {
                _control.setValidators([ Validators.required, this.ValidatePeriods.bind(this) ]);
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }            
        }
    }

    OnPeriodChange(valid){
        this.GroupForm.get('periods').setErrors(valid ? null : { 'periods': true });
        if (valid){
            this.GroupForm.markAsDirty();
        }
    }

    OnAddPeriod(){
        let _periods = this.GroupForm.get('periods').value;

        let _period = new FamilyPeriod(null, this.data);
        if (_period){
            _periods.push(_period);
        }

        this.GroupForm.get('periods').setValue(_periods);
        this.GroupForm.markAsDirty();

        this.change.markForCheck();        
    }

    OnDelPeriod(period){
        let _periods = this.GroupForm.get('periods').value;
        let _idx = _periods.indexOf(period);
        if (_idx != -1){
            _periods.splice(_idx, 1);
        }

        this.GroupForm.get('periods').setValue(_periods);
        this.GroupForm.markAsDirty();

        this.change.markForCheck();
    }

    private _selectedproducts = null;
    get SelectProducts(){
        return this._selectedproducts;
    }

    set SelectProducts(value){
        this._selectedproducts = value;
        this.change.markForCheck();
    }

    OnShowProducts(_familyitems = []){
        let _selectedproducts = [];
        for(let _familyitem of _familyitems){
            _selectedproducts.push(_familyitem.item);
        }

        this.SelectProducts = _selectedproducts;
    }

    OnHideProducts(){
        this.SelectProducts = null;
    }

    OnAddProducts(_items){
        let _current = this.GroupForm.get('fmitems').value;
        let _nextval = [];

        for(let _item of _items){
            let _familyproduct = null;

            for (let _familyitem of _current){
                if (_familyitem.item == _item){
                    _familyproduct = _familyitem;
                }
            }

            if (_familyproduct == null){   // not found: we must create a new family product
                _familyproduct = new FamilyProduct(null, this.data);
                if (_familyproduct){
                    _familyproduct.item = _item;
                    _familyproduct.status = 'AC';
                }
            }

            _nextval.push(_familyproduct);
        }

        this.GroupForm.get('fmitems').setValue(_nextval);
        this.GroupForm.markAsDirty();

        this.OnHideProducts();
    }

    private _ApplyFamilyPeriods(_family, periods){
        let _periods = [];

        if (!_family.allday){
            for(let _period of periods){
                _period.family = _family;
    
                let _copyof = _period.CopyOf;               
                if (_copyof){
                    _copyof.family = _period.family;
                    _copyof.ini = _period.ini;
                    _copyof.end = _period.end;
                    _copyof.weekdays = _period.weekdays;
                    _copyof.timezone = _period.timezone;
    
                    _periods.push(_copyof);
                }
                else {
                    _periods.push(_period);
                }
            }
        }

        // include all periods in the final list
        for(let _period of _periods){
            _family.AddPeriod(_period);
        }

        // remove periods not in the final list
        for (let _period of _family.periods){
            if (_periods.indexOf(_period) == -1){
                _period.status = 'DE';
            }            
        }
    }

    private _ApplyFamilyProducts(_family, familyitems){
        let _currentitems = []; 
        for(let _familyitem of _family.items){
            if (_familyitem.IsValid){
                _currentitems.push(_familyitem);
            }
        }

        let _familyitems = [];
        for (let _item of familyitems){
            _item.family = _family;

            let _copyof = _item.CopyOf;               
            if (_copyof){
                _copyof.family = _item.family;
                _copyof.status = _item.status;
                _copyof.sort = _item.sort;
                _copyof.preselect = _item.preselect;
                _copyof.product = _item.product;

                _familyitems.push(_copyof);
            }
            else {
                _familyitems.push(_item);
            }
        }

        // insert the included items into the family
        for(let _familyitem of _familyitems){
            let _pos = _currentitems.indexOf(_familyitem);
            if (_pos != -1){
                _currentitems.splice(_pos, 1);
            }
            else {
                _family.AddItem(_familyitem);
            }
        }

        // delete the removed items from the family
        for(let _familyitem of _currentitems){
            _familyitem.status = 'DE';
        }
    }

    async OnGroupAccept(){
        this._editable.status = 'DE';

        this._item.photo = this.GroupForm.get('photo').value;
        if ((!this._item.hasPhoto) || (this._item.photo == this._nophoto)) {
            this._item.photo = this._item.base64 = null;
        }

        this._item.name = this.GroupForm.get('name').value;
        this._item.description = this.GroupForm.get('description').value;

        if (this.GroupForm.get('isfamily').value){
            let _family = this._item.family;

            _family.product = this._item;
            _family.allday = this.GroupForm.get('allday').value;
            _family.status = 'AC'

            this._ApplyFamilyPeriods(_family, this.GroupForm.get('periods').value);
            this._ApplyFamilyProducts(_family, this.GroupForm.get('fmitems').value);

            this._item.family = _family;
        }
        else {
            let _family = this._item.family;
            if (_family){
                _family.status = 'DE';
            }            
        }

        this.data.Clock.ClockEnable('group-accept', false);     // pause all view refreshes
        await this._item.DoCommit();

        this.GroupForm.markAsPristine();
        this.OnClose();
        this.data.Clock.ClockEnable('group-accept', true);     // resume all view refreshes
    }

    get CanRemoveGroup(){
        let _group: Product = (this._editable && this._editable.CopyOf) ? this._editable.CopyOf as Product : this._editable;
        if ((this.EditMode) && (_group)) {
            if (_group.ToCommit || !_group.IsValid){
                return false;
            }

            if (_group.IsPurchased){
                for(let _item of this._place.products){
                    if ((_item.IsPurchased) && (_item.IsChild(_group))){
                        return false;   // not empty
                    }
                }
                return true;    // empty group
            }
        }
        return false;
    }

    async OnGroupRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_group_delete_confirm'),
            message: this.lang.tr('@place_group_delete_message', [ this._editable.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._editable.status = 'DE';

                this.data.Clock.ClockEnable('group-remove', false);     // pause all view refreshes

                this.GroupForm.markAsPristine();
                this._OnClose();

                this._item.status = 'DE'
                this._item.DoCommit();

                this._place.DoRefresh('PRODUCT');

                this.data.Clock.ClockEnable('group-remove', true);      // resume all view refreshes                
            }
        });

        await alert.present();
    }

    private SortFamilies(dirty = true){
        let _families = this.GroupForm.get('fmitems').value;

        // sort family items
        _families.sort((a, b) => {
            return Number(a.sort) - Number(b.sort);
        });
        
        // reindex categories and options
        let _selorder = 0;
        for(let _family of _families){
            _family.sort = _selorder++;
        }

        if (dirty){
            this.GroupForm.updateValueAndValidity();       
            this.GroupForm.markAsDirty();
        }
    }

    OnFamilyDrop(event, families){
        let _prev = event.previousIndex;
        let _next = event.currentIndex;

        let _element = families[_prev];
        families.splice(_prev, 1);
        families.splice(_next, 0, _element);
        
        for (let _idx=0; _idx < families.length; _idx++){
            families[_idx].sort = _idx;
        }

        this.SortFamilies();
    }

    // Ordena alfabéticamente (A-Z) los productos del grupo por su nombre y
    // reindexa el campo `sort`, igual que un drag manual, para que quede
    // persistido. Reutiliza SortFamilies() que reordena la vista por `sort`.
    OnSortFamiliesAlpha(){
        let _families = this.GroupForm.get('fmitems').value;
        if (!_families || _families.length < 2){
            return;
        }

        _families.sort((a, b) =>
            ((a.item && a.item.name) || '').localeCompare(((b.item && b.item.name) || ''), 'es', { sensitivity: 'base' }));

        for (let _idx=0; _idx < _families.length; _idx++){
            _families[_idx].sort = _idx;
        }

        this.SortFamilies();
    }

    /**********************************/
    /* GENERIC EDITION                */
    /**********************************/    

    private async _CommitGeneric(){
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            this._editable.CopyOf = null;

            // adjust extras (old product is now the new product)
            for(let _extra of this._place.extras){
                let _updated = _extra.SetProduct(this._item, this._editable);

                _transaction.SendCommit(_extra);
                if (_updated){  // add the copied offer to the place
                    _extra.status = 'DE';
                    this._place.AddExtra(_updated);
                    _transaction.SendCommit(_updated);
                }
            }
                                
            // adjust specific product printers (old product is now the new product)
            if (this._place.till){
                for (let _printer of this._place.till.printers){
                    let _updated = _printer.SetProduct(this._item, this._editable);

                    _transaction.SendCommit(_printer);
                    if (_updated){  // add the copied offer to the place
                        _printer.status = 'DE';
                        this._place.till.AddPrinter(_updated);
                        _transaction.SendCommit(_updated);
                    }
                }
            }

            // delete the previous product
            this._item.status = 'DE';

            // commit the old product (if it was loaded)
            if (this._item.IsLoaded){
                _transaction.SendCommit(this._item);
            }

            // reference the original product with the editable 
            if (this._editable.IsValid){
                this._editable.Overwrite();  
            }

            // commit the new product (if it is not deleted)
            if (this._editable.IsValid){
                _transaction.SendCommit(this._editable);
            }

            return _transaction.Flush(false);
        }

        return false;
    }

    async OnGenericAccept(){
        this._editable.photo = this.GenericForm.get('photo').value;
        if ((!this._editable.hasPhoto) || (this._editable.photo == this._nophoto)) {
            this._editable.photo = this._editable.base64 = null;
        }
        
        this._editable.name = this.GenericForm.get('name').value;
        this._editable.description = this.GenericForm.get('description').value;
        this._editable.type = this.GenericForm.get('type').value;

        this._editable.HideApp = true;
        this._editable.HideWeb = true;
        this._editable.HidePdf = true;

        this.data.Clock.ClockEnable('generic-accept', false);     // pause all view refreshes
        await this._CommitGeneric();
        this._place.AddProduct(this._editable);
        this.data.Clock.ClockEnable('generic-accept', true);     // resume all view refreshes

        this.GenericForm.markAsPristine();
        this._onPanel.emit({
            scnd: 'product',
            args: {
                product: this._editable
            }
        });              
    }

    get CanRemoveGeneric(){
        let _item: Product = (this._editable && this._editable.CopyOf) ? this._editable.CopyOf as Product : this._editable;
        if ((this.EditMode) && (_item)) {
            if (_item.IsPurchased && (!_item.ToInsert) && (_item.IsValid)){
                return true;
            }
        }
        return false;
    }

    async OnGenericRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_generic_delete_confirm'),
            message: this.lang.tr('@place_generic_delete_message', [ this._editable.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this.data.Clock.ClockEnable('generic-remove', false);     // pause all view refreshes

                this.GenericForm.markAsPristine();
                this._OnClose();

                this._editable.status = 'DE';
                await this._CommitGeneric();
                this._place.DoRefresh('PRODUCT');

                this.data.Clock.ClockEnable('generic-remove', true);      // resume all view refreshes                
            }
        });

        await alert.present();
    }

    /**********************************/
    /* PRODUCT EDITION                */
    /**********************************/    

    get AvailableOptions(){
        return this._editable.categories.some(
        (category) => {
            for(let option of category.options){
                if (option['form'].valid){
                    return true;
                }
                return false;
            }
        });
    }

    /* product actions (available) */

    get CheckAvailable(){
        let _parent = this._item.parent;
        while(_parent != null){
            if (!_parent.available){
                return false;
            }
            
            _parent = _parent.parent;
        }
        return true;
    }

    SetProductAvailable(value){
        this._item.available = value;
        this._item.DoCommit();
    }

    async OnProductAvailable(value){
        if (!this.CanAvailable){
            return;     // the user does not have permissions for this action 
        }

        if (value == false){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_product_unavailable_title'),
                message: this.lang.tr('@place_product_unavailable_message', [ this._editable.name ]),
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then(async (detail:any) => {
                if (detail.data == true){
                    this.SetProductAvailable(false);
                }
            });
    
            await alert.present();    
        }
        else {
            this.SetProductAvailable(true);
        }
    }

    /* product actions (publish) */

    get HideApp(){
        return this._editable.HideApp;
    }

    OnHideApp(){
        this._editable.HideApp = !this._editable.HideApp;
        this.ProductForm.markAsDirty();
    }

    get HidePdf(){
        return this._editable.HidePdf;
    }

    OnHidePdf(){
        this._editable.HidePdf = !this._editable.HidePdf;
        this.ProductForm.markAsDirty();
    }

    get HideWeb(){
        return this._editable.HideWeb;
    }

    OnHideWeb(){
        this._editable.HideWeb = !this._editable.HideWeb;
        this.ProductForm.markAsDirty();
    }

    /* product actions (edit) */

    private _ApplyFormData(){
        if (this._editable.status == 'CT'){
            this._editable.status = 'AC';
        }

        this._editable.photo = this.ProductForm.get('photo').value;
        this._editable.name = this.ProductForm.get('name').value;
        this._editable.code = this.ProductForm.get('code').value;
        this._editable.price = Number(this.ProductForm.get('price').value);
        // null when the override checkbox is off — let the product inherit
        // the place default. Otherwise commit the radio's value (0 stays 0).
        if (this.ProductForm.get('taxcheck').value){
            this._editable.taxrate = Number(this.ProductForm.get('taxrate').value);
        }
        else {
            this._editable.taxrate = null;
        }
        this._editable.description = this.ProductForm.get('description').value;
        this._editable.type = this.ProductForm.get('type').value;
        this._editable.MarketPrice = this.ProductForm.get('market').value;
        this._editable.IsWeighted = this.ProductForm.get('weight').value;

        if (this._editable.MarketPrice){
            this._editable.price = null;            
        }

        for(let _category of this._editable.categories){
            // update the option form data into the product
            for(let _option of _category.options){
                _option.active = (_option['form'].get('check').value);
                _option.name = _option['form'].get('name').value;
                _option.description = _option['form'].get('desc').value;                
                _option.price = Number(_option['form'].get('price').value);

                // activate catalog options when active
                if ((_option.status == 'CT') && (_option.active)){
                    _option.status = 'AC';
                }
                
                // determine final price for negative values
                if (_option['form'].get('negate').value) {
                    _option.price = 0 - _option.price;
                }
            }
        }
    }

    private async _CommitProduct(){
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            // Creación rápida: materializar AQUÍ las asociaciones atajo→familia
            // pendientes (no antes, para no dejar items colgando si se cancela). Se
            // crea el FamilyProduct con su relación `family` explícita (AddItem no la
            // rellena) y evitando duplicar una pertenencia existente (que puede
            // referenciar el ORIGINAL del atajo vía CopyOf, no la copia editada).
            let _quickFamilies: Family[] = [];
            for(let _link of this._quickFamilyLinks){
                // Si el atajo se borró (DelSelect) antes de guardar el producto, su
                // link queda huérfano: no crear un FamilyProduct para un atajo que ya
                // no existe.
                if (!_link.select || !_link.select.IsValid || (this._editable.selects.indexOf(_link.select) < 0)){
                    continue;
                }
                let _items = _link.family.items || [];
                let _exists = _items.some(it => it.IsValid && ((it.item == _link.select) || (_link.select.CopyOf && (it.item == _link.select.CopyOf))));
                if (!_exists){
                    let _fp = new FamilyProduct(null, this.data);
                    _fp.family = _link.family;     // AddItem no rellena la relación inversa
                    _fp.item = _link.select;
                    _fp.status = 'AC';
                    _fp.sort = _items.length;
                    _link.family.AddItem(_fp);
                }
                if (_quickFamilies.indexOf(_link.family) < 0){
                    _quickFamilies.push(_link.family);
                }
            }

            // Snapshot del mapeo atajo COPIADO -> ORIGINAL *antes* de cualquier SetProduct:
            // copiar el producto entero (o una oferta/extra/impresora) hace que, al limpiarse
            // el CopyOf de la copia resultante (DataObject.CopyOf=null se propaga a los hijos),
            // también se borre el CopyOf de los selects editados. Si lo leyéramos más abajo
            // dentro de los loops podría devolver null y saltarse la migración del atajo,
            // dejando familias/impresoras apuntando a un select que se va a borrar.
            let _selectCopyMap: Array<{ orig: ProductSelect, copy: ProductSelect }> = [];
            for (let _select of this._editable.selects){
                let _copyof = _select.CopyOf as ProductSelect;
                if (_copyof){
                    _selectCopyMap.push({ orig: _copyof, copy: _select });
                }
            }

            // adjust families products and selects  (old product is now new product)
            for(let _family of this._place.families){
                let _updated = _family.SetProduct(this._item, this._editable);

                for (let _map of _selectCopyMap){
                    _updated = _family.SetProduct(_map.orig, _map.copy) || _updated;
                }

                // Commitear también las familias con asociación de alta rápida, aunque
                // SetProduct no cambiara nada (si no, el nuevo FamilyProduct no se
                // persistiría: la familia pertenece a otro producto-grupo).
                if (_updated || (_quickFamilies.indexOf(_family) >= 0)){
                    _transaction.SendCommit(_family);
                };
            }

            // adjust offers (old product is now the new product)
            for(let _offer of this._place.offers){
                let _updated = _offer.SetProduct(this._item, this._editable);

                if (_updated){  // add the copied offer to the place
                    _offer.status = 'DE';
                    _transaction.SendCommit(_offer);

                    this._place.AddOffer(_updated);
                    _transaction.SendCommit(_updated);
                }
            }

            // adjust extras (old product is now the new product)
            for(let _extra of this._place.extras){
                let _updated = _extra.SetProduct(this._item, this._editable);

                if (_updated){  // add the copied extra to the place
                    _extra.status = 'DE';
                    _transaction.SendCommit(_extra);

                    this._place.AddExtra(_updated);
                    _transaction.SendCommit(_updated);
                }
            }

            // adjust specific product printers (old product is now the new product)
            if (this._place.till){
                for (let _printer of this._place.till.printers){
                    let _updated = _printer.SetProduct(this._item, this._editable);

                    // Migrar también los ATAJOS asignados (CopyOf): sobre la copia nueva
                    // si hubo cambio de producto, o in-place si no. Si no, el PrintProduct
                    // de atajo quedaría con un select borrado y la impresora, inválida.
                    let _target = _updated || _printer;
                    let _selectChanged = false;
                    let _survivingOrig = [];
                    for (let _map of _selectCopyMap){   // snapshot tomado antes de los SetProduct
                        _survivingOrig.push(_map.orig);
                        _selectChanged = _target.SetSelect(_map.orig, _map.copy) || _selectChanged;
                    }
                    // Atajos BORRADOS (en el original pero sin copia superviviente):
                    // quitar los PrintProducts que los referencian; si no, al marcar el
                    // producto 'DE' el atajo quedaría huérfano e invalidaría la impresora.
                    for (let _origselect of this._item.selects){
                        if (_survivingOrig.indexOf(_origselect) < 0){
                            _selectChanged = _target.SetSelect(_origselect, null) || _selectChanged;
                        }
                    }

                    if (_updated){  // add the copied printer to the place
                        _printer.status = 'DE';
                        _transaction.SendCommit(_printer);

                        this._place.till.AddPrinter(_updated);
                        _transaction.SendCommit(_updated);
                    }
                    else if (_selectChanged){   // solo cambiaron atajos (in-place)
                        _transaction.SendCommit(_printer);
                    }
                }
            }

            this._editable.CopyOf = null;

            // delete the previous product
            this._item.Catalog = null;
            this._item.status = 'DE';

            // commit the old product (if it was loaded)
            if (!this._item.IsCatalog && this._item.IsLoaded){
                _transaction.SendCommit(this._item);
            }

            // add the parent dependencies into de place
            if (this._editable.status != 'DE'){
                let _parent = this._editable.parent;
                while (_parent != null){
                    if (_parent.IsCatalog){
                        _parent.status = 'AC';
                        _transaction.SendCommit(_parent);
                    }
    
                    this._place.AddProduct(_parent);
                    _parent = _parent.parent;
                }    
            }

            // commit the updated product
            _transaction.SendCommit(this._editable);

            // Limpiar las familias pendientes SOLO si el commit fue bien: si Flush
            // falla/rollback, un reintento debe volver a enviarlas (si no, el
            // FamilyProduct nuevo se perdería).
            let _result = await _transaction.Flush(false);
            if (_result){
                this._quickFamilyLinks = [];
            }
            else {
                // Commit fallido: _CommitMap ya limpió el ToInsert del FamilyProduct
                // nuevo antes de que Flush fallara, así que quedó "commiteado" en
                // memoria pero SIN persistir -> ni el sync ni un reintento lo
                // reenviarían. Re-marcamos sucio el subárbol de esas familias
                // (ForceRecommit) para que se vuelva a enviar.
                for(let _family of _quickFamilies){
                    _family.ForceRecommit();
                }
            }
            return _result;
        }

        return false;
    }

    async OnProductAccept(){
        this._ApplyFormData();

        this._editable.photo = this.ProductForm.get('photo').value;
        if ((!this._editable.hasPhoto) || (this._editable.photo == this._nophoto)) {
            this._editable.photo = this._editable.base64 = null;
        }

        // remove categories without options
        let _categories = this._editable.categories.slice(0); 
        for(let _category of _categories){
            let _hasoptions = false;

            let _options = _category.options.slice(0); 
            for(let _option of _options){
                if (!_option.active){
                    _option.status = 'DE';
                }
                else {
                    _hasoptions = true;
                }
            }

            if (!_hasoptions){
                this._editable.DelCategory(_category);
            }
        }

        // remove unsatisfied dependencies
        for(let _category of _categories){
            let _depends = _category.depends.slice(0);
            for(let _depend of _depends){
                if (!_depend.option.IsValid){
                    _category.DelDepend(_depend);
                }
            }
        }
                
        // remove unavailable directs
        let _directs = this._editable.selects.slice(0);
        for(let _direct of _directs){
            if (!_direct.IsActive){
                this._editable.DelSelect(_direct);
            }
        }

        this.data.Clock.ClockEnable('product-accept', false);     // pause all view refreshes   
        let _catalog = this._editable.Catalog as Product;
        if (_catalog){
            this._editable.OnResolve();
        }

        let _commited = await this._CommitProduct();
        if (_commited){
            this._place.AddProduct(this._editable);
        }
        this.data.Clock.ClockEnable('product-accept', true)      // resume all view refreshes

        this.ProductForm.markAsPristine();
        this._onPanel.emit({
            scnd: 'product',
            args: {
                product: this._editable
            }
        });            
    }

    get CanDuplicate(){
        if (!this.EditMode || this._item.isgroup){
            return false;
        }

        if (this._item.ToInsert || this._item.IsCatalog) {
            return (this.ProductForm.dirty && this.ProductForm.valid);
        }

        return true;
    }

    OnProductDuplicate(){
        let _duplicate = this._editable.Copy();
        if (_duplicate){
            _duplicate.CopyOf = null;

            _duplicate.status = 'AC';
            _duplicate.name = this.lang.tr('@place_product_duplicate_name', [ _duplicate.name ]);
            _duplicate.price = null;
        }

        this._onPanel.emit({
            scnd: 'product',
            args: {
                product: _duplicate
            }
        });
    }

    get CanRemoveProduct(){
        let _item: Product = (this._editable && this._editable.CopyOf) ? this._editable.CopyOf as Product : this._editable;
        if ((this.EditMode) && (_item)) {
            if (_item.IsPurchased && (!_item.ToInsert) && (_item.IsValid)){
                return true;
            }
        }
        return false;
    }

    async OnProductRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_product_delete_confirm'),
            message: this.lang.tr('@place_product_delete_message', [ this._editable.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this.data.Clock.ClockEnable('product-remove', false);     // pause all view refreshes

                this.ProductForm.markAsPristine();
                this._OnClose();

                this._editable.status = 'DE';
                await this._CommitProduct();
                this._place.DoRefresh('PRODUCT');

                this.data.Clock.ClockEnable('product-remove', true)      // resume all view refreshes                
            }
        });

        await alert.present();
    }

    /**********************************/
    /* OPTIONS EDITION                */
    /**********************************/    

    SetOptionAvailable(option, value){
        option.available = value;
        
        // update the editable object
        for(let _category of this._editable.categories){
            for(let _option of _category.options){
                if (_option.IsCopyOf(option)){
                    _option.available = value;
                }
            }
        }

        // Devolver la promesa del commit para que el bulk pueda SERIALIZAR:
        // DoCommit empuja a una lista de cambios COMPARTIDA y persiste; lanzar
        // varios a la vez (activar/desactivar todas) los persistia desordenados.
        // Los callers individuales pueden seguir sin await (fire-and-forget).
        return option.DoCommit();
    }

    async OnOptionAvailable(option, value){
        if (!this.CanAvailable){
            return;     // the user does not have permissions for this action 
        }

        if (value == false){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_product_unavailable_title'),
                message: this.lang.tr('@place_product_unavailable_message', [ option.name ]),
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then(async (detail:any) => {
                if (detail.data == true){
                    this.SetOptionAvailable(option, false);
                }
            });

            await alert.present();
        }
        else {
            this.SetOptionAvailable(option, true);
        }
    }

    /* Activar / desactivar TODAS las opciones de un grupo de una vez. Pensado
       para menus del dia: desactivas el grupo entero (39 opciones) y activas
       solo las 5-6 de hoy, sin ir una a una. Al desactivar pide UN solo
       confirm para todo el grupo (no uno por opcion). */
    /* Un grupo puede tener opciones borradas/invalidas que no se listan ni se
       tocan; los botones "activar/desactivar todas" solo tienen sentido si hay
       al menos una opcion VALIDA (las mismas que muestra la vista y que procesa
       OnAllOptionsAvailable). Sin esto, un grupo con solo opciones invalidas
       mostraba los botones y al pulsarlos no pasaba nada. */
    CategoryHasOptions(category): boolean {
        return !!(category && category.options && category.options.some(_o => _o.IsValid));
    }

    /* Estado del toggle "Todas": ON si hay ALGUNA opcion valida disponible.
       Asi, desde un estado mixto (menu del dia ya puesto) un toque apaga todas
       (reset); desde todas apagadas, un toque las enciende. */
    CategoryAnyAvailable(category): boolean {
        return !!(category && category.options && category.options.some(_o => _o.IsValid && _o.available));
    }

    async OnCategoryToggle(category, ev){
        let _want = !!(ev && ev.detail && ev.detail.checked);
        // ionChange tambien salta al re-sincronizar [checked] por deteccion de
        // cambios (o si el confirm se cancela): si ya coincide con el estado
        // real, no hacemos nada (evita bucle y respeta el revert).
        if (_want === this.CategoryAnyAvailable(category)){
            return;
        }
        await this.OnAllOptionsAvailable(category, _want);
        this.change.markForCheck();
    }

    async OnAllOptionsAvailable(category, value){
        if (!this.CanAvailable){
            return;     // el usuario no tiene permisos para esta accion
        }

        // Solo las opciones VALIDAS (las mismas que se listan en la vista).
        let _options = (category.options || []).filter(_o => _o.IsValid);
        if (_options.length == 0){
            return;
        }

        if (value == false){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_product_unavailable_title'),
                message: this.lang.tr('@place_product_options_disable_all_message', [ category.name ]),
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then(async (detail:any) => {
                if (detail.data == true){
                    for (let _option of _options){
                        await this.SetOptionAvailable(_option, false);   // serializado
                    }
                }
            });

            await alert.present();
        }
        else {
            for (let _option of _options){
                await this.SetOptionAvailable(_option, true);   // serializado
            }
        }
    }

    @ViewChildren('optionname') optionnames!: QueryList<any>;

    private _setfocus = null;
    ngAfterViewChecked() {
        if (this._setfocus != null){
            for (let _optionnames of this.optionnames) {
                if (_optionnames.nativeElement.id == this._setfocus){
                    _optionnames.SetFocus();
                }
            }

            this._setfocus = null;
        }
    }

    OnAddOption(category){
        let _uuid = GenericUtils.uuidv4();

        let _option: ProductOption = new ProductOption(null, this.data);
        if (_option){
            _option['_uuid'] = _uuid;

            _option.product = this._editable;
            _option.active = true;
            _option.status = 'AC';
            _option.category = category;
            _option.sort = category.options.length;

            _option['edit'] = false;
            _option['form'] = this.OptionForm(_option);
            _option.product = this._editable;
        }

        this._setfocus = _uuid;
        category.AddOption(_option);
        this.SortCategories();
    }  

    OptionColor(option){
        return option.form.invalid ? 'danger' : 'primary';
    }

    OnOptionDrop(event, options){
        let _prev = event.previousIndex;
        let _next = event.currentIndex;
        
        let _element = options[_prev];
        options.splice(_prev, 1);
        options.splice(_next, 0, _element);
        
        for (let _idx=0; _idx < options.length; _idx++){
            options[_idx].sort = _idx;
        }

        this.SortCategories();
    }

    // Ordena alfabéticamente (A-Z) las opciones de una categoría por su nombre
    // y reindexa el campo `sort`, como un drag manual, para dejarlo persistido.
    OnSortOptionsAlpha(options){
        if (!options || options.length < 2){
            return;
        }

        options.sort((a, b) =>
            ((a.name) || '').localeCompare(((b.name) || ''), 'es', { sensitivity: 'base' }));

        for (let _idx=0; _idx < options.length; _idx++){
            options[_idx].sort = _idx;
        }

        this.SortCategories();
    }

    OnNegatePrice(option){
        let _neg = option.form.get('negate').value;
        _neg = !_neg;
        option.form.get('negate').setValue(_neg);

        this.OnPriceChange(option);
    }

    OnOptionChange(){
        this.ProductForm.updateValueAndValidity();
    }

    OnOptionCheck(){
        for(let _category of this._editable.categories){
            for(let _option of _category.options){
                _option.active = _option['form'].get('check').value;
            }
        }

        this.ProductForm.updateValueAndValidity();
        setTimeout(() => {
            this.change.markForCheck();
        }, 0);
    }

    OnAddOptionDescription(option){
        option['desc'] = option.description;
        option['edit'] = true;

        this.change.markForCheck();
    }

    OnAcceptOptionDescription(event, option){
        event.stopPropagation();
        
        option.description = option.form.get('desc').value;
        option['desc'] = null;
        option['edit'] = false;

        this.ProductForm.markAsDirty();
        this.change.markForCheck();
    }

    OnCancelOptionDescription(event, option){
        event.stopPropagation();

        option.form.get('desc').setValue(option['desc']);
        option['desc'] = null;
        option['edit'] = false;

        this.change.markForCheck();
    }

    /**********************************/
    /* CATEGORY EDITION               */
    /**********************************/      

    CategoryOptions(_category: ProductCategory){
        let _options = [];

        for (let _option of _category.options){
            if (_option.IsValid && _option.active){
                _options.push(_option);
            }
        }

        return _options;
    }

    OnCategory(category: ProductCategory = null){
        this._ApplyFormData();

        let _category = category;
        if (!_category){
            _category = new ProductCategory(null, this.data);
            if (_category){
                _category['form'] = this._CategoryForm(_category);
                _category.product = this._editable;
                _category['candelete'] = false;
            }
        }

        this._category = _category;
   
        for(let _category of this._editable.categories){
            for(let _option of _category.options){
                _option['isdepend'] = (this._category.HasCondition(_option) != null);
            }
        }

        this.View = 'CATEGORY';
    }

    private SortCategories(dirty = true){
        // sort categories and options
        this._editable.categories.sort((a, b) => {
            return Number(a.sort) - Number(b.sort);
        });
        
        for(let _category of this._editable.categories){
            _category.options.sort((a, b) => {
                return Number(a.sort) - Number(b.sort);
            });
        }
        
        // reindex categories and options
        let _ctgorder = 0;
        for(let _category of this._editable.categories){
            _category.sort = _ctgorder++;
            
            let _optorder = 0;
            for(let _option of _category.options){
                _option.sort = _optorder++;
            }
        }

        if (dirty){
            this.ProductForm.updateValueAndValidity();       
            this.ProductForm.markAsDirty();
        }

        this.change.markForCheck();
    }

    CanUp(category){
        return (category.sort > 0);
    }

    OnCategoryUp(category){
        for(let _category of this._editable.categories){
            if (_category.sort == (category.sort - 1)){
                _category.sort +=  1;
            }
        }

        category.sort -= 1;        
        this.SortCategories();
    }
    
    CanDown(category){
        return (category.sort < this._editable.categories.length - 1); 
    }

    OnCategoryDown(category){
        for(let _category of this._editable.categories){
            if (_category.sort == (category.sort + 1)){
                _category.sort -=  1;
            }
        }

        category.sort += 1;
        this.SortCategories();
    }

    OnSelectDependency(option){
        let _prev = (this.category.HasCondition(option) != null);
        if (_prev != option['isdepend']){
            this.CategoryForm.markAsDirty();
        }        
    }

    OnCategoryChanged(){
        let _fxmin = this.CategoryForm.get('fxmin').value;
        let _fxmax = this.CategoryForm.get('fxmax').value;

        if ((_fxmin === null) && (_fxmax === null)){
            this.CategoryForm.get('fxmin').setValue(0);
            this.CategoryForm.get('fxmax').setValue(1);
        }

        this.CategoryForm.updateValueAndValidity();
    }

    OnCategoryAccept(){
        this.ProductForm.markAsDirty();

        this._category.name = this.CategoryForm.get('name').value;
        this._category.type = this.CategoryForm.get('type').value;
        this._category.fxmin = this.CategoryForm.get('fxmin').value;
        this._category.fxmax = this.CategoryForm.get('fxmax').value;
        
        // if not stabished add the category at the end
        if (this._category.sort === null){
            this._category.sort = this._editable.categories.length;
        }

        for(let _category of this._editable.categories){
            for(let _option of _category.options){
                if (_option['isdepend']){
                    this.category.AddCondition(_option);
                }
                else {
                    this.category.DelCondition(_option);
                }
            }
        }

        this._category.status = 'AC';

        this._category['candelete'] = true;
        this._editable.AddCategory(this._category);
        this.CategoryForm.markAsPristine();

        this.OnClose();
    }

    OnCategoryCancel(){
        this.CategoryForm.markAsPristine();
        this.OnClose();
    }

    get CanRemoveCategory(){
        let _category: ProductCategory = (this._category && this._category.CopyOf) ? this._category.CopyOf as ProductCategory : this._category;
        if (_category){
            return (!_category.ToInsert || !('candelete' in _category) || _category['candelete']) && (_category.IsValid);
        }
        return false;
    }

    async OnCategoryRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_category_delete_confirm'),
            message: this.lang.tr('@place_category_delete_message', [ this._category.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail.data == true){
                this._editable.DelCategory(this._category);
                this.CategoryForm.markAsPristine();

                this.ProductForm.markAsDirty();
                this.OnClose();
            }
        });

        await alert.present();
    }

    /**********************************/
    /* ONECLICK EDITION               */
    /**********************************/      

    private _CategoryOptions(category){
        let _disable = false;
        if (category.type == 'SINGLE'){
            for(let _option of category.options){
                _option['checked'] = (_option == category['selected']);
            }
        }

        if (category.type == 'FIXED'){
            let _count = 0;
            for (let option of category.options){
                if ((option['checked']) && (option.IsAvailable)){
                    _count++;
                }
            }

            _disable = (_count >= category.fxmax);
            for (let option of category.options){
                option['disable'] = _disable && !option['checked'];
            }
        }
    }

    OnDirect(select: ProductSelect = null) {
        this._ApplyFormData();

        let _select = select;
        if (!_select){
            _select = new ProductSelect(null, this.data);
            if (_select){
                _select.product = this._editable;
                _select.photo = this._editable.photo;
                _select.code = this._editable.code;
                _select.sort = this._editable.selects.length;
                _select['candelete'] = false;

                this._OneClickForm(_select);
            }
        }

        this._oneclick = _select;

        // Creación rápida: familia opcional, y reset del autollenado de nombre.
        this._quickFamily = null;
        this._lastAutoName = '';

        for(let _category of this._editable.categories){
            for(let _option of _category.options){
                _option['checked'] = false;
                _option['disable'] = false;
            }

            for(let _option of _select.options){
                _option.option['checked'] = _option.IsValid;
            }

            // Holders transitorios para "crear opción nueva": en SINGLE uno
            // (newopt), en MULTIPLE/FIXED una lista (newextras, varios a la vez). La
            // opción NO se crea en el modelo hasta aceptar, así que cancelar no deja
            // basura.
            _category['newopt'] = this._newOptionForm();
            _category['newextras'] = [];

            if (_category.type == 'SINGLE'){
                _category['selected'] = null;
                for(let _option of _category.options){
                    if (_option['checked']) {
                        _category['selected'] = _option;
                    }
                }
            }
            else {
                // MULTIPLE/FIXED: el desplegable multi-selección se enlaza a este
                // array; lo sembramos con las opciones ya marcadas.
                _category['selectedMulti'] = _category.options.filter(o => o['checked']);
            }

            this._CategoryOptions(_category);
        }

        this.View = 'ONECLICK';
    }

    IsSattisfied(category){
        let _hasOptions = false;
        for(let _option of category.options){
            if (_option.IsAvailable){
                _hasOptions = true;
            }
        }
        
        if (!_hasOptions){
            return false;
        }

        if (!category.depends || (category.depends.length == 0)){
            return true;
        }

        let _sattisfied = category.depends.some(
        (depend) => {
            return (depend.option['checked'] == true);
        });

        // disable al checked options if not sattisfied
        if (!_sattisfied){
            for(let _option of category.options){
                _option['checked'] = false;
            }
            // Mantener el desplegable multi en sync con los 'checked' que acabamos de
            // limpiar: si no, al volver a mostrarse el grupo el select enseñaría
            // opciones que SelectedOptions/guardado ya no ven (y Accept quedaría
            // habilitado soltando extras en silencio).
            if (category.type != 'SINGLE'){
                category['selectedMulti'] = [];
            }
        }

        return _sattisfied;
    }

    OnOptionSelected(category, _option){
        let _name = this.OneClickForm.get('name').value;
        if ((_name == null) || (_name == '')){
            if (category.type == 'SINGLE'){
                this.OneClickForm.get('name').setValue(category['selected'].name);
            }
            else {
                this.OneClickForm.get('name').setValue(_option ? _option.name : null);
            }
        }
        
        setTimeout(() => {
            this._CategoryOptions(category);
            this.OneClickForm.markAsDirty();
            this.change.markForCheck();    
        }, 0);
    }

    // Cambio en el desplegable de un grupo SINGLE (elegir existente o "crear nueva").
    OnSingleOptionSelected(category){
        let _sel = category['selected'];
        if (_sel === this.QUICK_NEW){
            // "crear nueva": ninguna opción existente marcada; el nombre/precio se
            // teclean en los inputs inline (category['newopt']).
            for(let _option of category.options){
                _option['checked'] = false;
            }
            if (!category['newopt']){
                category['newopt'] = this._newOptionForm();
            }
        }
        else {
            // opción existente: marcarla y autorrellenar el nombre del atajo si está vacío.
            this._CategoryOptions(category);
            let _name = this.OneClickForm.get('name').value;
            if (((_name == null) || (_name == '')) && _sel){
                this.OneClickForm.get('name').setValue(_sel.name);
            }
        }
        setTimeout(() => {
            this.OneClickForm.markAsDirty();
            this.change.markForCheck();
        }, 0);
    }

    // Al teclear en el inline de "crear nueva": el atajo copia el nombre COMPLETO de
    // la opción nueva mientras el usuario no lo haya editado a mano (se detecta porque
    // el nombre del atajo deja de coincidir con el último autollenado).
    // Copia el nombre tecleado en la opción/extra nuevo al nombre del ATAJO: SIEMPRE
    // que se escriba abajo, el de arriba se actualiza (a petición del usuario).
    private _mirrorAtajoName(value){
        this.OneClickForm.get('name').setValue(value || '');
        this._lastAutoName = value || '';
    }

    OnQuickNewChanged(category, fromName = false){
        if (fromName && category['newopt']){
            this._mirrorAtajoName(category['newopt'].get('name').value || '');
        }
        this.OneClickForm.markAsDirty();
        this.change.markForCheck();
    }

    // Opciones nuevas pendientes (grupos SINGLE con "crear nueva" ya rellenada): para
    // mostrarlas en el resumen "Tus opciones para ..." con su nombre y precio, aunque
    // todavía no existan en el modelo.
    get PendingNewOptions(){
        let _list = [];
        for(let _category of this._editable.categories){
            if (!this.IsSattisfied(_category)){
                continue;
            }
            if ((_category['selected'] === this.QUICK_NEW) && this._quickNewValid(_category)){
                _list.push({
                    name: _category['newopt'].get('name').value,
                    price: Math.abs(Number(_category['newopt'].get('price').value))
                });
            }
            for(let _extra of (_category['newextras'] || [])){
                if (this._holderValid(_extra)){
                    _list.push({
                        name: _extra.get('name').value,
                        price: Math.abs(Number(_extra.get('price').value))
                    });
                }
            }
        }
        return _list;
    }

    // Cambio en el desplegable MULTI-selección de un grupo MULTIPLE/FIXED: vuelca la
    // selección (array de opciones) a los flags 'checked' que usa el resto (precio,
    // resumen, AddSelection al aceptar).
    OnMultiSelected(category){
        let _sel = (category['selectedMulti'] || []).slice();
        // "Crear nueva" dentro del desplegable es una ACCIÓN, no un valor: añade una
        // fila de extra nuevo y se quita de la selección (no se queda marcada).
        if (_sel.indexOf(this.QUICK_NEW) >= 0){
            _sel = _sel.filter(x => x !== this.QUICK_NEW);
            setTimeout(() => {
                category['selectedMulti'] = _sel;
                this.change.markForCheck();
            }, 0);
            this.OnAddNewExtra(category);
        }
        for(let _option of category.options){
            _option['checked'] = (_sel.indexOf(_option) >= 0);
        }
        this._CategoryOptions(category);   // FIXED: recalcula 'disable' por fxmax
        this.OneClickForm.markAsDirty();
        this.change.markForCheck();
    }

    // ¿Un holder {name, price} es válido?
    private _holderValid(form){
        if (!form){
            return false;
        }
        let _name = form.get('name').value;
        let _price = form.get('price').value;
        return !!(_name && String(_name).trim() && (_price != null) && (_price !== '') && !isNaN(Number(_price)));
    }

    // ¿El holder de "opción nueva" de un grupo SINGLE es válido (nombre + precio)?
    private _quickNewValid(category){
        return this._holderValid(category['newopt']);
    }

    // Extras nuevos (grupos MULTIPLE/FIXED): añadir/quitar un holder {name, price}.
    OnAddNewExtra(category){
        if (!category['newextras']){
            category['newextras'] = [];
        }
        category['newextras'].push(this._newOptionForm());
        this.OneClickForm.markAsDirty();
        this.change.markForCheck();
    }
    OnRemoveNewExtra(category, idx){
        if (category['newextras'] && (idx >= 0) && (idx < category['newextras'].length)){
            category['newextras'].splice(idx, 1);
        }
        this.OneClickForm.markAsDirty();
        this.change.markForCheck();
    }
    // Recalcular (precio/resumen) al teclear un extra nuevo. Si se pasa el holder (al
    // cambiar el NOMBRE), también se copia al nombre del atajo (como en SINGLE).
    OnQuickExtraChanged(extra = null){
        if (extra){
            this._mirrorAtajoName(extra.get('name').value || '');
        }
        this.OneClickForm.markAsDirty();
        this.change.markForCheck();
    }

    private _validSingleSelection(_category){
        if (this.IsSattisfied(_category)){
            if (_category['selected'] === this.QUICK_NEW){
                return this._quickNewValid(_category);
            }
            return (_category['selected']);
        }
        return true;
    }

    private _validFixedSelection(_category){
        let _count = 0;
        for (let _option of _category.options){
            if (_option['checked']){
                _count++;
            }
        }
        // Las opciones nuevas pendientes (crear nueva) también cuentan para el cardinal
        // FIXED: así una pendiente puede satisfacer fxmin y no se puede superar fxmax
        // (p.ej. fxmax ya alcanzado + una nueva -> inválido, Accept deshabilitado).
        for (let _extra of (_category['newextras'] || [])){
            if (this._holderValid(_extra)){
                _count++;
            }
        }

        return (_count >= _category.fxmin) && (_count <= _category.fxmax);
    }

    get IsOneClickCompleted(){
        return this._editable.categories.every(
        (category) => {
            if (!this.IsSattisfied(category)){
                return true;
            }
            
            switch(category.type){
                case 'SINGLE':
                    return this._validSingleSelection(category);
                case 'MULTIPLE':
                    return true;   
                case 'FIXED':
                    return this._validFixedSelection(category);   
            }

            return false;
        });
    }

    get SelectedOptions() {
        let _options = [];
        for(let _category of this._editable.categories){
            if (this.IsSattisfied(_category)){
                for(let _option of _category.options){
                    if (_option['checked']){
                       _options.push(_option);
                   }
               }   
            }
        }
        return _options;
    }

    get SelectedPrice() {
        let _price: number = Number(this._editable.price);
        for(let _option of this.SelectedOptions){
            _price += Number(_option.price);
        }
        // opciones nuevas pendientes (crear nueva) de grupos SINGLE: aún no están en
        // el modelo, pero su precio ya cuenta en el total mostrado. Solo si el grupo
        // está satisfecho (un grupo condicional oculto no debe sumar).
        for(let _category of this._editable.categories){
            if (!this.IsSattisfied(_category)){
                continue;
            }
            // opción nueva de un grupo SINGLE
            if ((_category['selected'] === this.QUICK_NEW) && this._quickNewValid(_category)){
                _price += Math.abs(Number(_category['newopt'].get('price').value));
            }
            // extras nuevos de un grupo MULTIPLE/FIXED
            for(let _extra of (_category['newextras'] || [])){
                if (this._holderValid(_extra)){
                    _price += Math.abs(Number(_extra.get('price').value));
                }
            }
        }
        return _price;
    }

    // Crea en el MODELO las opciones nuevas (grupos SINGLE con "crear nueva"): hasta
    // ahora eran solo un holder {name,price} para no dejar basura si se cancelaba.
    // Al materializarlas con category.AddOption aparecen en la lista de opciones del
    // producto; luego se preseleccionan en el atajo como una opción normal.
    // Crea un ProductOption en la categoría desde un holder {name, price}. Precio
    // positivo (alta rápida). markChecked=true lo marca seleccionado (extras MULTIPLE).
    private _createOptionFromHolder(category, holder, markChecked): ProductOption {
        let _option: ProductOption = new ProductOption(null, this.data);
        _option.product = this._editable;
        _option.active = true;
        _option.status = 'AC';
        _option.category = category;
        _option.sort = category.options.length;
        _option.name = String(holder.get('name').value).trim();
        _option.price = Math.abs(Number(holder.get('price').value));
        // form coherente con el resto (lo lee _ApplyFormData al commitear).
        _option['edit'] = false;
        _option['form'] = this.OptionForm(_option);
        category.AddOption(_option);
        if (markChecked){
            _option['checked'] = true;
        }
        return _option;
    }

    private _materializeQuickOptions(){
        for(let _category of this._editable.categories){
            // Un grupo condicional oculto (no satisfecho) no debe crear opciones.
            if (!this.IsSattisfied(_category)){
                continue;
            }

            if (_category.type == 'SINGLE'){
                if ((_category['selected'] === this.QUICK_NEW) && this._quickNewValid(_category)){
                    let _option = this._createOptionFromHolder(_category, _category['newopt'], false);
                    // seleccionarla: pasa a estar "checked" y deja de ser "crear nueva".
                    _category['selected'] = _option;
                    this._CategoryOptions(_category);
                    // si el atajo aún no tiene nombre, hereda el de la nueva opción.
                    let _name = this.OneClickForm.get('name').value;
                    if ((_name == null) || (_name == '')){
                        this.OneClickForm.get('name').setValue(_option.name);
                    }
                    _category['newopt'] = this._newOptionForm();
                }
            }
            else {
                // MULTIPLE/FIXED: materializar cada extra nuevo válido, marcado.
                for(let _extra of (_category['newextras'] || [])){
                    if (this._holderValid(_extra)){
                        this._createOptionFromHolder(_category, _extra, true);
                    }
                }
                _category['newextras'] = [];
            }
        }
    }

    async OnOneClickAccept(){
        this.ProductForm.markAsDirty();

        // Materializar las opciones nuevas ANTES de preseleccionarlas.
        this._materializeQuickOptions();

        this._oneclick.photo = this.OneClickForm.get('photo').value;
        if (!this._oneclick.hasPhoto){
            this._oneclick.photo = this._oneclick.base64 = null;
        }

        this._oneclick.name = this.OneClickForm.get('name').value;
        this._oneclick.code = this.OneClickForm.get('code').value;
        this._oneclick.description = this.OneClickForm.get('description').value;

        // remove previous options and add current ones
        for(let _option of this._oneclick.options){
            _option.status = 'DE';
        }

        for(let _option of this.SelectedOptions){
            if (_option['checked']){
                this._oneclick.AddSelection(_option);
            }
        }

        this._oneclick['candelete'] = true;
        this._OneClickForm(this._oneclick);

        this.editable.AddSelect(this._oneclick);

        // Añadir el atajo a la familia elegida (opcional): se deja PENDIENTE y se
        // materializa en _CommitProduct, para no mutar una familia real si luego se
        // cancela el editor del producto.
        if (this._quickFamily){
            let _dup = this._quickFamilyLinks.some(l => (l.family == this._quickFamily) && (l.select == this._oneclick));
            if (!_dup){
                this._quickFamilyLinks.push({ family: this._quickFamily, select: this._oneclick });
            }
        }

        this.OneClickForm.markAsPristine();

        this.OnClose();
    }

    get CanRemoveOneClick(){
        let _oneclick: ProductSelect = (this._oneclick && this._oneclick.CopyOf) ? this._oneclick.CopyOf as ProductSelect : this._oneclick;
        if (_oneclick){
            return (!_oneclick.ToInsert || !('candelete' in _oneclick) || _oneclick['candelete']) && (_oneclick.IsValid);
        }
        return false;
    }

    async OnOneClickRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_select_delete_confirm'),
            message: this.lang.tr('@place_select_delete_message', [ this._oneclick.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail.data == true){
                this._editable.DelSelect(this._oneclick);
                this.OneClickForm.markAsPristine();

                this.ProductForm.markAsDirty();
                this.OnClose();
            }
        });

        await alert.present();
    }

    private SortOneClick(dirty = true){
        // sort categories and options
        this._editable.selects.sort((a, b) => {
            return Number(a.sort) - Number(b.sort);
        });
        
        // reindex categories and options
        let _selorder = 0;
        for(let _select of this._editable.selects){
            _select.sort = _selorder++;
        }

        if (dirty){
            this.ProductForm.updateValueAndValidity();       
            this.ProductForm.markAsDirty();
        }
    }

    OnOneClickDrop(event, selects){
        let _prev = event.previousIndex;
        let _next = event.currentIndex;

        let _element = selects[_prev];
        selects.splice(_prev, 1);
        selects.splice(_next, 0, _element);
        
        for (let _idx=0; _idx < selects.length; _idx++){
            selects[_idx].sort = _idx;
        }

        this.SortOneClick();
    }

    // Ordena alfabéticamente (A-Z) los atajos de configuración (selects) por su
    // nombre y reindexa el campo `sort`, como un drag manual, para persistirlo.
    OnSortOneClickAlpha(){
        let _selects = this._editable.selects;
        if (!_selects || _selects.length < 2){
            return;
        }

        _selects.sort((a, b) =>
            ((a.name) || '').localeCompare(((b.name) || ''), 'es', { sensitivity: 'base' }));

        for (let _idx=0; _idx < _selects.length; _idx++){
            _selects[_idx].sort = _idx;
        }

        this.SortOneClick();
    }
} 
